import {
	type MouseEvent,
	type MouseEventHandler,
	type ReactNode,
	type Ref,
	useEffect,
	useState,
} from 'react';

import Button, {
	type ButtonProps,
	type ButtonSize,
	type ButtonVariant,
} from 'components/Button';
import Icon from 'components/Icon';
import { usePrevious, useTimeout } from 'hooks';
import type { WithOptional } from 'types';
import { cn } from 'utils/classNames';
import { ignorePromiseRejection, is, sleep } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export type ActionButtonVariant = ButtonVariant;
export type ActionButtonState = 'failure' | 'idle' | 'loading' | 'success';

// TODO: Should type away some states that are not relevant for text buttons,
// like failureLabel/hasVisibleText/showSuccess/successLabel.
export interface BaseProps extends Omit<ButtonProps, 'onClick'> {
	/** Button text */
	children: ReactNode;

	/** Custom state to show. */
	customState?: ActionButtonState;

	/** Custom ARIA label for when the button is in a failure state */
	failureLabel?: string;

	/** If the text (children) should be visible. If false, an icon should be set. */
	hasVisibleText?: boolean;

	/** Custom ARIA label for when the button is in a loading state */
	loadingLabel?: string;

	/** Number of milliseconds the spinner must be shown (set to 0 to not use a minimum time) */
	minimunLoadingTime?: number;

	/** Click callback. Promise resolution status triggers success or failure. */
	onClick: (e: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;

	/** Callback for when onClick triggers an error */
	onError?: (error?: unknown) => void;

	/** Callback for when onClick completed successfully */
	onSuccess?: () => void;

	ref?: Ref<HTMLButtonElement>;

	/** Should the success checkmark be shown? */
	showSuccess?: boolean;

	/** Custom ARIA label for when the button is in a success state */
	successLabel?: string;

	/** Button style variant */
	variant?: ActionButtonVariant;
}

// Optional onClick for submit buttons.
export interface SubmitProps extends WithOptional<BaseProps, 'onClick'> {
	type: 'submit';
}

export type Props = BaseProps | SubmitProps;

/** Button with a spinner and feedback message. */
export default function ActionButton({
	children,
	className,
	customState,
	failureLabel,
	hasVisibleText = true,
	loadingLabel,
	minimunLoadingTime = 1400,
	onClick,
	onError,
	onSuccess,
	ref,
	showSuccess: showSuccessProp,
	size = 'md',
	successLabel,
	variant = 'secondary',
	...buttonProps
}: Props) {
	const { t } = useI18n();
	const [state, setState] = useState<ActionButtonState>('idle');
	const [customStateCanTransition, setCustomStateCanTransition] =
		useState<boolean>(true);
	const prevCustomState = usePrevious(customState);
	const isTextButton = is.oneOf(variant, 'text', 'text-inline', 'text-inherit');
	const showSuccess = isTextButton ? false : (showSuccessProp ?? true);
	const showFailure = !isTextButton;

	// Go back to idle after success or failure.
	useTimeout(
		() => {
			setState('idle');
		},
		is.oneOf(state, 'success', 'failure') ? 3000 : null,
	);

	// Handle custom state changes
	useEffect(() => {
		if (!customStateCanTransition || !customState) {
			return;
		}

		// Only allow transition to success or failure from loading.
		// TODO: This is needed because of the old machine behavior that
		// allowed a pattern like:
		// `customState={isLoading ? 'loading' : isError ? 'failure' : 'success'}`
		// The proper way should be to require a correct customState that falls
		// back to idle instead of success.
		if (
			is.oneOf(customState, 'success', 'failure') &&
			prevCustomState !== 'loading'
		) {
			setState('idle');
			return;
		}

		setState(customState);

		// Make sure we wait the minimunLoadingTime before allowing
		// transition to other state.
		if (customState === 'loading') {
			setCustomStateCanTransition(false);
			setTimeout(() => {
				setCustomStateCanTransition(true);
			}, minimunLoadingTime);
		}
	}, [
		customState,
		prevCustomState,
		customStateCanTransition,
		minimunLoadingTime,
	]);

	const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		// Ignore clicks if currently loading.
		if (state === 'loading' || customState === 'loading') {
			return;
		}

		if (customState) {
			const result = onClick?.(e);
			if (is.promise(result)) {
				ignorePromiseRejection(result);
			}
			return;
		}

		setState('loading');

		if (onClick) {
			Promise.all([sleep(minimunLoadingTime), onClick(e)])
				.then(() => {
					onSuccess?.();
					setState(showSuccess ? 'success' : 'idle');
				})
				.catch((error) => {
					onError?.(error);
					setState(showFailure ? 'failure' : 'idle');
				});
		}
	};

	const iconSizeClasses: Record<ButtonSize, string> = {
		sm: 'size-5',
		md: 'size-7',
		lg: 'size-8',
	};
	const iconSizeClass = iconSizeClasses[size];
	const iconBaseClasses = cn('block', iconSizeClass);
	const iconPositionClasses =
		'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2';

	const buttonVariant = state === 'failure' ? 'branded' : variant;
	const buttonIconClasses = cn(
		state !== 'idle' && 'invisible',
		!hasVisibleText && 'm-0',
	);

	return (
		<Button
			{...buttonProps}
			ref={ref}
			size={size}
			onClick={handleClick}
			variant={buttonVariant}
			leadingIconClass={buttonIconClasses}
			trailingIconClass={buttonIconClasses}
			aria-label={
				state === 'loading'
					? (loadingLabel ?? t('general_loading_text'))
					: state === 'failure'
						? (failureLabel ?? t('general_action_failure_text'))
						: state === 'success'
							? (successLabel ?? t('general_action_success_text'))
							: undefined
			}
			// Could possibly add aria-disabled as well, if all action buttons should
			// be treated as disabled when loading? The custom state prop makes it
			// a bit tricky.
			aria-busy={
				state === 'loading' || customState === 'loading' ? 'true' : undefined
			}
			className={cn(
				'relative',
				!hasVisibleText && [
					// Center icon and remove padding.
					'items-center justify-center p-0',
					// Expand hit area for accessibility.
					size === 'sm' && 'after:absolute after:-inset-1.5',
				],
				className,
			)}
		>
			{!isTextButton && state === 'loading' && (
				<span
					className={cn('inline-block', iconPositionClasses, iconSizeClass)}
				>
					<svg
						viewBox="25 25 50 50"
						className={cn(iconBaseClasses, 'animate-spinnerRotate')}
					>
						<circle
							cx="50"
							cy="50"
							r="20"
							fill="none"
							strokeWidth="4"
							strokeMiterlimit="10"
							strokeDasharray="1px, 200px"
							strokeDashoffset="0px"
							stroke="currentColor"
							strokeLinecap="round"
							className="animate-spinnerDash"
						/>
					</svg>
				</span>
			)}
			{!isTextButton && state === 'success' && (
				<svg
					viewBox="0 0 26 18"
					className={cn(iconBaseClasses, iconPositionClasses)}
				>
					<path
						fill="none"
						stroke="currentColor"
						strokeDasharray="3rem"
						strokeDashoffset="3rem"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M1 8.98L9.01 17 25 1"
						className="animate-spinnerStroke"
					/>
				</svg>
			)}
			{!isTextButton && state === 'failure' && (
				<svg
					viewBox="0 0 40 40"
					className={cn(iconBaseClasses, iconPositionClasses)}
				>
					<path
						fill="none"
						stroke="currentColor"
						strokeDasharray="3rem"
						strokeDashoffset="3rem"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3"
						d="M 10,10 L 30,30 M 30,10 L 10,30"
						className="animate-spinnerStroke"
					/>
				</svg>
			)}
			{isTextButton && state === 'loading' && (
				<Icon name="loading" className={iconPositionClasses} />
			)}
			<span
				className={cn(
					!hasVisibleText && 'sr-only',
					hasVisibleText && [
						'flex items-center justify-center',
						state !== 'idle' && 'invisible',
					],
				)}
			>
				{children}
			</span>
		</Button>
	);
}
ActionButton.displayName = 'ActionButton';
